import { format } from "date-fns";
import { ArrowLeftIcon } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import { Card } from "@/components/card";
import { CopyButton } from "@/components/copy-button";
import { LoadingScreen } from "@/components/loading-screen";
import { Button, buttonVariants } from "@/components/ui/button";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useGetSession } from "@/features/session/api/get-session";
import { AttendeesList } from "@/features/session/components/attendees-list";
import { OrganisationsList } from "@/features/session/components/organisation-list";
import { RoomSystemsList } from "@/features/session/components/room-system-list";
import { SessionActions } from "@/features/session/components/session-actions";
import { SessionCard } from "@/features/session/components/session-card";
import { cn } from "@/lib/utils";
import { RoleEnum } from "@classview/shared-types";

export default function SessionPage() {
  const { sessionId } = useParams();
  const {
    data: session,
    isLoading,
    isSuccess,
    isError,
  } = useGetSession(sessionId ?? "");
  const { hasPermission } = useOrganisation();

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Button variant="link" asChild>
            <Link to="/sessions">
              <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back to session
            </Link>
          </Button>
        </div>
        <OnboardingHeader title="Session Details" className="mb-6" />

        <LoadingScreen />
      </div>
    );
  }

  if (isError || !isSuccess) {
    return <Navigate to="/sessions" />;
  }

  const sessionDuration = `${format(session.start, "HH:mm")} - ${format(
    session.end,
    "HH:mm",
  )}`;

  const canEditSession = hasPermission([RoleEnum.CALL_MANAGEMENT]);

  return (
    <div>
      <div className="mb-6">
        <Button variant="link" asChild>
          <Link to="/sessions">
            <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back to session
          </Link>
        </Button>
      </div>
      <div className="mb-6 flex items-center">
        <OnboardingHeader title="Session Details" />
        {!session.eventId && canEditSession && (
          <SessionActions
            sessionId={session.id}
            recurringId={session.recurringDetails?.id}
            className="ml-2"
            endDate={new Date(session.end)}
          />
        )}
      </div>
      <div className="gap-4 space-y-4 md:grid md:grid-cols-12 md:space-y-0">
        <div className="col-span-7 space-y-4">
          <Card>
            <Card.Content className="mb-0 space-y-4">
              <SessionCard session={session} />
              <div className="flex items-center justify-end gap-2">
                <Button asChild>
                  <Link to={`/r/${session.room.key}`} target="_blank">
                    Join Call
                  </Link>
                </Button>
              </div>
            </Card.Content>
            <Card.Header title="How to Join" />
            <Card.Content className="space-y-6">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Web or mobile browser
                </p>
                <p className="mb-1 text-sm">
                  <Link
                    to={`/r/${session.room.key}`}
                    target="_blank"
                    className={cn(
                      buttonVariants({ variant: "link" }),
                      "h-auto p-0 font-normal",
                    )}
                  >
                    {window.location.origin}/r/{session.room.key}
                  </Link>
                </p>
                <CopyButton
                  toCopy={`${window.location.origin}/r/${session.room.key}`}
                >
                  Copy Link
                </CopyButton>
              </div>
              {/* <div>
                <p className="text-sm font-medium text-foreground">Telephone</p>
                <p className="text-sm">
                  Call:{" "}
                  <a
                    href="tel:+4403002010142"
                    className={cn(
                      buttonVariants({ variant: "link" }),
                      "h-auto p-0 font-normal",
                    )}
                  >
                    +44 (0) 300 201 0142
                  </a>{" "}
                  and then enter extension #{session.room.extension}
                </p>
              </div> */}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header title="Session Details" />
            <Card.Content>
              <p className="text-md mb-4 font-semibold text-foreground">
                {sessionDuration} {session.name}
              </p>
              <p className="text-xs text-muted">{session.description}</p>

              <Link
                to={`/recordings/session/${session.id}`}
                className={cn(buttonVariants({ variant: "link" }), "p-0")}
              >
                <Button variant="link">View Session Recording</Button>
              </Link>
            </Card.Content>
          </Card>
        </div>
        <div className="col-span-5 space-y-4">
          <Card>
            <Card.Header title="Attendees" />
            <Card.Content>
              <AttendeesList attendees={session.attendees ?? []} />
            </Card.Content>
          </Card>
          {session.roomSystems.length > 0 && (
            <Card>
              <Card.Header title="Room Systems" />
              <Card.Content>
                <RoomSystemsList roomSystems={session.roomSystems} />
              </Card.Content>
            </Card>
          )}
          {session.invitedOrganisations.length > 0 && (
            <Card>
              <Card.Header title="Invited External Organisations" />
              <Card.Content>
                <OrganisationsList
                  organisations={session.invitedOrganisations}
                />
              </Card.Content>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
